"use client";

import { Accordion as AccordionPrimitive } from "@base-ui/react/accordion";
import { ChevronDownIcon } from "lucide-react";
import type * as React from "react";
import { cn } from "@/lib/utils";

type AccordionValue = string | string[];

type AccordionProps = Omit<
  React.ComponentProps<typeof AccordionPrimitive.Root>,
  "defaultValue" | "multiple" | "onValueChange" | "value"
> & {
  collapsible?: boolean;
  defaultValue?: AccordionValue;
  multiple?: boolean;
  onValueChange?: (value: AccordionValue) => void;
  type?: "multiple" | "single";
  value?: AccordionValue;
};

function toValueArray(value: AccordionValue | undefined) {
  if (value === undefined) {
    return undefined;
  }
  if (Array.isArray(value)) {
    return value;
  }
  return value ? [value] : [];
}

function fromValueArray(value: string[], multiple: boolean) {
  return multiple ? value : (value[0] ?? "");
}

function Accordion({
  defaultValue,
  multiple,
  onValueChange,
  type,
  value,
  ...props
}: AccordionProps) {
  const isMultiple = multiple ?? type === "multiple";

  return (
    <AccordionPrimitive.Root
      data-slot="accordion"
      defaultValue={toValueArray(defaultValue)}
      multiple={isMultiple}
      onValueChange={(nextValue) => {
        onValueChange?.(fromValueArray(nextValue, isMultiple));
      }}
      value={toValueArray(value)}
      {...props}
    />
  );
}

function AccordionItem({
  className,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Item>) {
  return (
    <AccordionPrimitive.Item
      className={cn("border-b last:border-b-0", className)}
      data-slot="accordion-item"
      {...props}
    />
  );
}

function AccordionTrigger({
  className,
  children,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Trigger>) {
  return (
    <AccordionPrimitive.Header className="flex">
      <AccordionPrimitive.Trigger
        className={cn(
          "flex flex-1 items-start justify-between gap-4 rounded-md py-4 text-left text-sm font-medium transition-all outline-none hover:underline focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 aria-disabled:pointer-events-none aria-disabled:opacity-50 [&[data-panel-open]>svg]:rotate-180",
          className,
        )}
        data-slot="accordion-trigger"
        {...props}
      >
        {children}
        <ChevronDownIcon className="pointer-events-none size-4 shrink-0 translate-y-0.5 text-muted-foreground transition-transform duration-200" />
      </AccordionPrimitive.Trigger>
    </AccordionPrimitive.Header>
  );
}

function AccordionContent({
  className,
  children,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Panel>) {
  return (
    <AccordionPrimitive.Panel
      className="overflow-hidden text-sm"
      data-slot="accordion-content"
      {...props}
    >
      <div
        className={cn(
          "h-(--accordion-panel-height) pt-0 pb-4 transition-[height] duration-200 data-ending-style:h-0 data-starting-style:h-0",
          className,
        )}
      >
        {children}
      </div>
    </AccordionPrimitive.Panel>
  );
}

export { Accordion, AccordionContent, AccordionItem, AccordionTrigger };
